import './css/tokens.css';
import './css/themes.css';
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import { ThemeProvider, useTheme } from './components/ThemeContext';
import './css/App.css';
import './css/skeleton.css';
import AppRoutes from './routes/AppRoutes';
import HeroSection from './components/HeroSection';
import ErrorBoundary from './components/ErrorBoundary';
import { AssistantSkeleton } from './components/skeletons';

const PortfolioAssistant = React.lazy(() => import('./components/PortfolioAssistant'));

function DeferredAssistant({ currentPage }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(() => setReady(true), { timeout: 2000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(() => setReady(true), 1200);
    return () => window.clearTimeout(timeoutId);
  }, []);

  if (!ready) {
    return <AssistantSkeleton />;
  }

  return (
    <Suspense fallback={<AssistantSkeleton />}>
      <PortfolioAssistant currentPage={currentPage} />
    </Suspense>
  );
}

// Create a wrapper component to access theme context and location
function AppContent() {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [guestName, setGuestName] = useState(null);
  const [currentPage, setCurrentPage] = useState('portfolio');

  // Detect current page based on route - Updated for all routes
  useEffect(() => {
    const path = location.pathname;
    
    // Exact route matching for all pages
    if (path === '/about') setCurrentPage('about');
    else if (path === '/skills') setCurrentPage('skills');
    else if (path === '/projects') setCurrentPage('projects');
    else if (path === '/github-projects') setCurrentPage('github-projects');
    else if (path === '/contact') setCurrentPage('contact');
    else if (path === '/services') setCurrentPage('services');
    else if (path === '/resume') setCurrentPage('resume');
    else if (path === '/references') setCurrentPage('references');
    else if (path === '/add-references') setCurrentPage('add-references');
    else if (path.startsWith('/edit-reference')) setCurrentPage('edit-reference');
    else if (path === '/blogs') setCurrentPage('blogs');
    else if (path === '/news') setCurrentPage('news');
    else if (path === '/weather') setCurrentPage('weather');
    else if (path === '/reviews') setCurrentPage('reviews');
    else if (path === '/uc') setCurrentPage('uc');
    else setCurrentPage('portfolio');
  }, [location]);

  useEffect(() => {
    const storedGuestName = localStorage.getItem('guestName');
    if (storedGuestName) {
      setGuestName(storedGuestName);
    }
  }, []);

  const handleLogout = () => {
    setGuestName(null);
    localStorage.removeItem('guestName');
  };

  const handleGuestAccessGranted = (name) => {
    setGuestName(name);
    localStorage.setItem('guestName', name);
  };

  return (
    <div className={`App ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      {!guestName ? (
        <HeroSection onSuccess={handleGuestAccessGranted} />
      ) : (
        <>
          <Layout user={guestName} onLogout={handleLogout}>
            <AppRoutes
              guestName={guestName}
              handleLogout={handleLogout}
              handleGuestAccessGranted={handleGuestAccessGranted}
            />
          </Layout>
          
          <DeferredAssistant currentPage={currentPage} />
        </>
      )}
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
